import React, { ReactNode } from 'react';
import { z } from 'zod';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useQuery } from '@apollo/client';
import { SelectField } from 'components/form';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomDialog } from 'components/shared/Dialog';
import { Info, ReportProblem } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';

import { bpfAPI } from '../../../api';
import { BpfCloseReasonType } from '../../../types';
import { Q_VO } from '../../../../../queries/common';
import { useDealLifeCycle } from '../../../api/dealLifeCycle';

const contentByType: Record<
  BpfCloseReasonType,
  { label: string; subTitle: string; icon: ReactNode; notificationMessage: string }
> = {
  [BpfCloseReasonType.Won]: {
    subTitle: 'Select “Won” reason',
    icon: <Info color="primary" />,
    label: 'Closed Won Reasons ',
    notificationMessage: 'This deal will be closed as won.',
  },
  [BpfCloseReasonType.Lost]: {
    subTitle: 'Select “Lost” reason',
    icon: <ReportProblem color="warning" />,
    label: 'Closed Lost Reasons',
    notificationMessage:
      'This deal will be closed as lost. This action ends the sales process and prevents further stage progression.',
  },
};

const closeDealWithReasonFormSchema = z.object({
  parentType: z.string(),
  stageId: z.string().uuid(),
  reasonId: z.string().uuid(),
  parentId: z.string().uuid(),
});

type CloseDealWithReasonFormValues = z.infer<typeof closeDealWithReasonFormSchema>;

export const CloseDealWithReasonDialog = () => {
  const {
    isOpen,
    handleClose,
    data: { bpfId, stageId, parentType, parentId, closeReasonId, type },
  } = useModal(ModalsKeys.CloseDealDialog);

  const { loading: dealLifeCycleLoading, actions: dealLifeCycleActions } = useDealLifeCycle();

  const { data: response, loading: closeReasonsLoading } = useQuery(bpfAPI.fetchCloseReasonsOptions, {
    notifyOnNetworkStatusChange: true,
    variables: {
      type,
      id: bpfId,
    },
  });

  const { control, handleSubmit } = useForm<CloseDealWithReasonFormValues>({
    resolver: zodResolver(closeDealWithReasonFormSchema),
    defaultValues: {
      stageId,
      parentId,
      parentType,
      reasonId: closeReasonId ?? '',
    },
  });

  const currentReasonId = useWatch({ control, name: 'reasonId' });

  const submit = async (values: CloseDealWithReasonFormValues) => {
    await dealLifeCycleActions.handleCloseDeal(
      { ...values, reasonStatus: type === 'WON' ? 'SUCCESS' : 'FAILED' },
      () => ({
        awaitRefetchQueries: true,
        refetchQueries: [Q_VO, bpfAPI.fetchVOTemplatesAndStages, bpfAPI.fetchCSTemplatesAndStages],
      })
    );

    handleClose();
  };

  return (
    <CustomDialog
      isOpen={isOpen}
      onClose={handleClose}
      label={type === BpfCloseReasonType.Lost ? 'Mark as Lost' : 'Mark as Won'}
      actions={
        <Stack direction="row" gap="0.5rem">
          <Button
            size="small"
            variant="contained"
            disabled={!currentReasonId}
            onClick={handleSubmit(submit)}
            loading={dealLifeCycleLoading.isDealClosing}
          >
            Confirm
          </Button>

          <Button size="small" onClick={handleClose}>
            Cancel
          </Button>
        </Stack>
      }
    >
      <Stack gap="1rem">
        <Stack direction="row" alignItems="center" gap="0.5rem">
          {contentByType[type].icon}

          <Typography variant="body2">{contentByType[type].notificationMessage}</Typography>
        </Stack>

        <Typography variant="subtitle2">{contentByType[type].subTitle}</Typography>

        <SelectField
          required
          label="Reason"
          name="reasonId"
          control={control}
          variant="outlined"
          isLoading={closeReasonsLoading}
          options={response?.bpf?.options}
          disabled={!response?.bpf?.options?.length || dealLifeCycleLoading.isDealClosing}
        />
      </Stack>
    </CustomDialog>
  );
};
